import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { adminSession, storeAppUrl } from '../../services/adminApi';
import {
  FaShieldAlt, FaGem, FaTachometerAlt, FaUsers,
  FaCreditCard, FaMoneyBillWave, FaDatabase, FaSignOutAlt, FaBars, FaTimes,
  FaExternalLinkAlt, FaCrown,
} from 'react-icons/fa';

const NAV = [
  { to: '/overview', label: 'Overview', icon: FaTachometerAlt, end: true },
  { to: '/users', label: 'Accounts & Users', icon: FaUsers, end: false },
  { to: '/subscription', label: 'Plans & Subscription', icon: FaCreditCard, end: false },
  { to: '/payments', label: 'Payments', icon: FaMoneyBillWave, end: false },
  { to: '/system', label: 'Backup & System', icon: FaDatabase, end: false },
];

const linkCls = (active: boolean) =>
  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
    active
      ? 'bg-gradient-to-r from-amber-500/20 to-amber-500/5 text-amber-300 border border-amber-500/30'
      : 'text-slate-300 hover:bg-white/5 hover:text-white border border-transparent'
  }`;

const SidebarContent: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const user = adminSession.getUser();

  const logout = () => {
    adminSession.clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className='h-full bg-slate-950 flex flex-col overflow-y-auto'>
      {/* Brand */}
      <div className='px-5 py-5 border-b border-white/10'>
        <div className='flex items-center gap-2.5'>
          <div className='w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0'>
            <FaGem className='text-white text-sm' />
          </div>
          <div className='min-w-0'>
            <p className='text-white font-bold leading-tight truncate'>ApniCRM</p>
            <p className='text-amber-300/90 text-[10px] font-bold uppercase tracking-widest'>
              Admin Control
            </p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className='flex-1 px-3 py-4 space-y-1'>
        <p className='px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500'>
          Manage
        </p>
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) => linkCls(isActive)}
          >
            <item.icon className='shrink-0' />
            <span className='truncate'>{item.label}</span>
          </NavLink>
        ))}

        <div className='pt-4 mt-4 border-t border-white/10 space-y-1'>
          <p className='px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500'>
            Store App
          </p>
          <a
            href={storeAppUrl()}
            className='flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/5 hover:text-white transition-all border border-transparent'
          >
            <FaExternalLinkAlt className='shrink-0' />
            <span className='truncate'>Open CRM Store</span>
          </a>
        </div>
      </nav>

      {/* Bottom user card */}
      <div className='p-3 border-t border-white/10'>
        <div className='flex items-center gap-3 bg-white/5 rounded-2xl p-3'>
          <div className='w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold shrink-0'>
            {(user?.name || 'A').charAt(0).toUpperCase()}
          </div>
          <div className='flex-1 min-w-0'>
            <p className='text-white text-sm font-semibold truncate flex items-center gap-1'>
              {user?.name || 'Admin'} <FaCrown className='text-amber-400 text-[10px] shrink-0' />
            </p>
            <p className='text-slate-400 text-xs truncate'>{user?.email}</p>
          </div>
          <button
            onClick={logout}
            className='w-9 h-9 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center transition-colors shrink-0'
            title='Logout'
          >
            <FaSignOutAlt />
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminLayout: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const user = adminSession.getUser();

  return (
    <div className='min-h-screen md:grid md:grid-cols-[260px_1fr] bg-slate-100'>
      {/* Desktop sidebar */}
      <aside className='hidden md:block sticky top-0 h-screen'>
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <header className='md:hidden sticky top-0 z-40 bg-slate-950 px-4 py-3 flex items-center gap-3 safe-top'>
        <button
          onClick={() => setDrawerOpen(true)}
          className='w-10 h-10 rounded-xl bg-white/10 text-white flex items-center justify-center shrink-0'
          aria-label='Open menu'
        >
          <FaBars />
        </button>
        <div className='flex items-center gap-2 flex-1 min-w-0'>
          <div className='w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0'>
            <FaShieldAlt className='text-white text-xs' />
          </div>
          <div className='min-w-0'>
            <p className='text-white text-sm font-bold leading-tight truncate'>Admin Control</p>
            <p className='text-slate-400 text-[10px] truncate'>{user?.email}</p>
          </div>
        </div>
        <span className='text-[10px] font-bold px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0'>
          ADMIN
        </span>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className='md:hidden fixed inset-0 z-50'>
          <div
            className='absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in'
            onClick={() => setDrawerOpen(false)}
          />
          <div className='absolute inset-y-0 left-0 w-[280px] max-w-[85vw] shadow-2xl animate-slide-in-left'>
            <button
              onClick={() => setDrawerOpen(false)}
              className='absolute top-4 right-4 z-10 w-9 h-9 rounded-lg bg-white/10 text-white flex items-center justify-center'
              aria-label='Close menu'
            >
              <FaTimes />
            </button>
            <SidebarContent onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

      {/* Content */}
      <div className='min-w-0'>
        <main className='p-4 md:p-8 max-w-7xl mx-auto'>
          <Outlet />
        </main>
        <footer className='px-4 md:px-8 pb-6 text-center text-xs text-slate-400'>
          <FaShieldAlt className='inline mr-1 text-slate-300' />
          ApniCRM Admin Control · Accounts, plans, backups & store system
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;
